import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
  useReducer, // ← 추가
} from "react";
import { COLS, ROW_H, MAX_HISTORY } from "./constants";
import { layoutsEqual } from "./utils";

export function useGridBackground(alignMode, containerRef) {
  const [colWidth, setColWidth] = useState(0);
  const recalc = useCallback(() => {
    if (!containerRef.current) return;
    setColWidth((containerRef.current.offsetWidth || 0) / COLS);
  }, [containerRef]);

  useLayoutEffect(() => recalc(), [recalc]);
  useEffect(() => {
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [recalc]);

  return useMemo(() => {
    if (!alignMode || !colWidth) return {};
    const light = "rgba(0,0,0,0.06)",
      dark = "rgba(0,0,0,0.16)";
    return {
      backgroundImage: [
        `repeating-linear-gradient(0deg, ${light}, ${light} 1px, transparent 1px, transparent ${ROW_H}px)`,
        `repeating-linear-gradient(90deg, ${light}, ${light} 1px, transparent 1px, transparent ${colWidth}px)`,
        `repeating-linear-gradient(0deg, ${dark}, ${dark} 1px, transparent 1px, transparent ${
          ROW_H * 4
        }px)`,
        `repeating-linear-gradient(90deg, ${dark}, ${dark} 1px, transparent 1px, transparent ${
          colWidth * 4
        }px)`,
      ].join(", "),
    };
  }, [alignMode, colWidth]);
}

export function useAutosave(getBody, deps) {
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem("userform_draft", JSON.stringify(getBody()));
      } catch {}
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function useHistory(layout) {
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const push = (next) => {
    setHistory((h) =>
      layoutsEqual(next, layout)
        ? h
        : (h.length >= MAX_HISTORY ? h.slice(1) : h).concat([layout])
    );
    setFuture([]);
  };
  const undo = () =>
    setHistory((h) => {
      if (!h.length) return h;
      setFuture((f) => [layout, ...f]);
      return h.slice(0, -1).map((x) => x);
    });
  const redo = () =>
    setFuture((f) => {
      if (!f.length) return f;
      const [, ...rest] = f;
      setHistory((h) => [...h, layout]);
      return rest;
    });
  return { history, future, push, undo, redo, setHistory, setFuture };
}

export function useFormState(initial) {
  const initialState = {
    layout: [],
    blocks: {},
    values: {
      title: "",
      dateStr: "",
      editorHtml: "<p>본문을 입력하세요.</p>",
      note: "",
      photos: [],
      files: [],
    },
    customValues: {},
    tables: {},
    history: [],
    future: [],
    alignMode: false,
    dirty: false,
    ...initial,
  };

  function reducer(state, action) {
    switch (action.type) {
      case "SET_LAYOUT":
        return { ...state, layout: action.payload, dirty: true };
      case "PUSH_HISTORY":
        return {
          ...state,
          history: [...state.history, state.layout],
          future: [],
        };
      case "UNDO":
        if (!state.history.length) return state;
        return {
          ...state,
          future: [state.layout, ...state.future],
          layout: state.history[state.history.length - 1],
          history: state.history.slice(0, -1),
        };
      case "REDO":
        if (!state.future.length) return state;
        return {
          ...state,
          history: [...state.history, state.layout],
          layout: state.future[0],
          future: state.future.slice(1),
        };
      case "PATCH_VALUES":
        return {
          ...state,
          values: { ...state.values, ...action.payload },
          dirty: true,
        };
      case "PATCH_BLOCKS":
        return {
          ...state,
          blocks: { ...state.blocks, ...action.payload },
          dirty: true,
        };
      case "PATCH_CUSTOM":
        return {
          ...state,
          customValues: { ...state.customValues, ...action.payload },
          dirty: true,
        };
      case "PATCH_TABLES":
        return {
          ...state,
          tables: { ...state.tables, ...action.payload },
          dirty: true,
        };
      case "TOGGLE_ALIGN":
        return { ...state, alignMode: !state.alignMode };
      case "RESET_DIRTY":
        return { ...state, dirty: false };
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);

  const api = useMemo(
    () => ({
      setLayout: (l) => {
        dispatch({ type: "PUSH_HISTORY" });
        dispatch({ type: "SET_LAYOUT", payload: l });
      },
      patchValues: (v) => dispatch({ type: "PATCH_VALUES", payload: v }),
      patchBlocks: (b) => dispatch({ type: "PATCH_BLOCKS", payload: b }),
      patchCustom: (c) => dispatch({ type: "PATCH_CUSTOM", payload: c }),
      patchTables: (t) => dispatch({ type: "PATCH_TABLES", payload: t }),
      undo: () => dispatch({ type: "UNDO" }),
      redo: () => dispatch({ type: "REDO" }),
      toggleAlign: () => dispatch({ type: "TOGGLE_ALIGN" }),
      resetDirty: () => dispatch({ type: "RESET_DIRTY" }),
    }),
    []
  );

  return { state, ...api };
}
